import React from 'react'

// Utils
import { lighten } from 'polished'
import { isHexColor } from 'utils'

// Components
import { Simple } from '../Avatar'
import { PaddedContent } from '../Layout'
import { Spinner } from '../Indicators'
import { Bubble, BubbleStyle } from './Bubble'

export interface IHeroProps {
  backgroundImageUrl?: string
  loading?: boolean
  bubble?: string
  bubbleStyle?: BubbleStyle
  title?: string
  subtitle?: string
  customTitles?: JSX.Element[]
  customSubtitles?: JSX.Element[]
  secondarySubtitle?: string
  avatarColor?: string
  avatarUrl?: string
}

const setHeroBackgroundImage = (backgroundUrl: string = ''): string =>
  backgroundUrl ?? 'none'

const setHeroBackgroundColor = (avatarColor: string = ''): string =>
  isHexColor(avatarColor) ? lighten(0.5)(avatarColor) : '#fff'

const HERO_CONTAINER_STYLES =
  'flex bg-white relative overflow-hidden flex-shrink-0 m-0'

// :Before
const HERO_BACKGROUND_IMAGE_STYLES = 'absolute bg-cover bg-no-repeat'
const HERO_BACKGROUND_IMAGE_INLINE_STYLES = {
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: 'url(graphic-to-be-filtered.jpg)',
  filter: 'blur(7px) brightness(200%) opacity(20%) grayscale(100%)',
}

const HERO_CONTENT_STYLES = 'relative max-w-7xl mx-auto w-full'

const HERO_TITLE_STYLES =
  'text-secondary font-heading text-2xl font-semibold uppercase my-2 mx-0'

const HERO_SUBTITLE_STYLES =
  'text-primary text-base font-light font-heading uppercase mx-0 mt-0 mb-2'

/**
 * Renders a hero providing an overview of a given profile.
 * @param props Provides the loading state and profile to render.
 */
export const Hero: React.FC<IHeroProps> = ({
  loading,
  title,
  subtitle,
  secondarySubtitle,
  avatarColor,
  avatarUrl,
  backgroundImageUrl,
  bubble,
  bubbleStyle,
  customTitles,
  customSubtitles,
}) => {
  const PLACEHOLDER = '-----'

  return (
    <div
      className={HERO_CONTAINER_STYLES}
      style={{ backgroundColor: setHeroBackgroundColor(avatarColor) }}
    >
      <div
        className={HERO_BACKGROUND_IMAGE_STYLES}
        style={{
          ...HERO_BACKGROUND_IMAGE_INLINE_STYLES,
          backgroundImage: `url(${setHeroBackgroundImage(backgroundImageUrl)})`,
        }}
      />
      <div className={HERO_CONTENT_STYLES}>
        <PaddedContent>
          <div className="flex py-4 px-0">
            {loading ? (
              <Spinner size="3x" />
            ) : (
              <Simple
                color={avatarColor}
                name={title}
                imageUrl={avatarUrl}
                size={118}
                maxInitials={2}
              />
            )}
            <div className="my-auto ml-4">
              <div className="flex flex-col">
                {bubble ? (
                  <div className="mr-auto">
                    <Bubble bubbleStyle={bubbleStyle}>{bubble}</Bubble>
                  </div>
                ) : null}
                {customTitles ? (
                  customTitles.map((c, i) => (
                    <h2 className={HERO_TITLE_STYLES} key={`customTitle${i}`}>
                      {loading ? PLACEHOLDER : c}
                    </h2>
                  ))
                ) : (
                  <h2 className={HERO_TITLE_STYLES}>
                    {loading ? PLACEHOLDER : title}
                  </h2>
                )}
                {customSubtitles ? (
                  customSubtitles.map((c, i) => (
                    <h2
                      className={HERO_SUBTITLE_STYLES}
                      key={`customSubtitle${i}`}
                    >
                      {loading ? PLACEHOLDER : c}
                    </h2>
                  ))
                ) : (
                  <h2 className={HERO_SUBTITLE_STYLES}>
                    {loading ? PLACEHOLDER : subtitle ?? PLACEHOLDER}
                    {secondarySubtitle ? (
                      <>
                        <br />
                        {loading ? PLACEHOLDER : secondarySubtitle}
                      </>
                    ) : null}
                  </h2>
                )}
              </div>
            </div>
          </div>
        </PaddedContent>
      </div>
    </div>
  )
}

Hero.displayName = 'Content.Hero'
